import { useState, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Link2, Copy, Check, Loader2, ChevronDown, ChevronRight, Send, Building } from 'lucide-react';

/**
 * Панель для владельца тендера: список приглашённых поставщиков + кнопка
 * «Получить ссылку» рядом с каждым, открывающая окно с копируемой ссылкой.
 * Видна организатору-владельцу тендера и со-организаторам того же направления.
 * Супер-админу не показываем (для него уже есть подробный AdminInvitationsLog).
 */
export default function InvitationLinksPanel({ tender }) {
  const [open, setOpen] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(null);
  const [copied, setCopied] = useState(false);

  const invited = tender.invited_emails || [];

  const openLinkFor = useCallback(async (email) => {
    setLoadingEmail(email);
    setCopied(false);
    try {
      const { data } = await api.get(`/tenders/${tender.id}/invitation-link`, { params: { email } });
      setLinkData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingEmail(null);
    }
  }, [tender.id]);

  const copy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Ссылка скопирована');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Не удалось скопировать');
    }
  }, []);

  if (invited.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-blue-200" data-testid={`invitation-links-${tender.id}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors"
        data-testid={`invitation-links-toggle-${tender.id}`}
      >
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-800">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          <Link2 className="w-3.5 h-3.5" />
          Приглашения по ссылке
          <Badge variant="outline" className="ml-1 text-[10px] rounded-sm bg-white border-blue-300 text-blue-700 font-normal">
            {invited.length} участн.
          </Badge>
        </span>
        <span className="text-[10px] text-blue-700/70 font-normal">
          для ручной рассылки в мессенджере
        </span>
      </button>

      {open && (
        <div className="mt-2 border border-blue-200 rounded-sm bg-white divide-y divide-slate-100">
          {invited.map(email => (
            <div key={email} className="px-3 py-2 flex items-center gap-3 hover:bg-slate-50">
              <Building className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="flex-1 text-xs text-slate-700 truncate" title={email}>{email}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openLinkFor(email)}
                disabled={loadingEmail === email}
                data-testid={`get-invitation-link-${email}`}
                className="rounded-sm text-xs h-7 px-2.5 border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
              >
                {loadingEmail === email
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <><Link2 className="w-3 h-3 mr-1" /> Получить ссылку</>}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Модальное окно со ссылкой */}
      <Dialog open={!!linkData} onOpenChange={(v) => { if (!v) setLinkData(null); }}>
        <DialogContent className="max-w-lg" data-testid="invitation-link-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-4 h-4 text-blue-700" />
              Приглашение на тендер
            </DialogTitle>
            <DialogDescription>
              Скопируйте ссылку и отправьте поставщику любым удобным способом (мессенджер, чат, e-mail вручную и т.п.).
            </DialogDescription>
          </DialogHeader>

          {linkData && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="text-slate-500">Поставщик</div>
                <div className="col-span-2 text-slate-800 font-medium">
                  {linkData.company_name || <span className="italic text-slate-400">без названия</span>}
                </div>
                <div className="text-slate-500">Email</div>
                <div className="col-span-2 text-slate-700">{linkData.email}</div>
                <div className="text-slate-500">Язык</div>
                <div className="col-span-2">
                  <Badge variant="outline" className="text-[10px] rounded-sm bg-slate-50 border-slate-200">
                    {linkData.lang === 'ru' ? 'Русский' : 'English'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Тема (для email):</div>
                <div className="flex gap-2">
                  <Input value={linkData.subject} readOnly className="text-xs h-8 font-mono rounded-sm" data-testid="invitation-subject-input" />
                  <Button size="sm" variant="outline" onClick={() => copy(linkData.subject)}
                    className="rounded-sm h-8 px-2.5"
                    data-testid="copy-subject-btn">
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ссылка-приглашение:</div>
                <div className="flex gap-2">
                  <Input value={linkData.link} readOnly className="text-xs h-8 font-mono rounded-sm bg-blue-50/50 border-blue-200 text-blue-900"
                    data-testid="invitation-link-input"
                    onFocus={(e) => e.target.select()} />
                  <Button size="sm" onClick={() => copy(linkData.link)}
                    className={`rounded-sm h-8 px-3 ${copied ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#0046AD] hover:bg-[#003A8C]'}`}
                    data-testid="copy-link-btn">
                    {copied ? <><Check className="w-3 h-3 mr-1" /> Готово</> : <><Copy className="w-3 h-3 mr-1" /> Копировать</>}
                  </Button>
                </div>
              </div>

              <div className="rounded-sm bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
                <strong>Подсказка:</strong> ссылка ведёт на главную страницу платформы с правильным языком интерфейса. После входа в систему поставщик попадёт в свой кабинет и увидит этот тендер в списке приглашений.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
